import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import {
    updateState,
    getChipsLabel,
    getThemesFilterOptions,
    createUpdatedChips,
    isChipActive,
    hasActiveChips,
} from '@helpers/filters';
import { formatTimeframeChipValue } from '@helpers/dates';
import { FiltersIntegrationType } from '@constants/integrations';
import { useTranslation, useLocations } from '@hooks';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    themes = {},
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
    applyChipsSignal = 0,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const [filterChips, setFilterChips] = useState({
        companies: [],
        integrations: [],
        themes: [],
        rating: [],
        timeframe: {},
        onlyFeatured: false,
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { getLocationsOptions, locationsMap } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    useEffect(() => {
        if (applyChipsSignal > 0) {
            applyChips();
        }
    }, [applyChipsSignal]);

    const options = useMemo(() => {
        //locationsOptions
        const filterCb = l => l?.profiles?.length > 0 && l?.profiles.some(p => p.type in FiltersIntegrationType);
        const locationsOptions = getLocationsOptions(filterCb);
        //integrationsOptions
        const integrationsOptions = Array.from(
            new Set(
                filter.companies
                    .map(item => locationsMap[item.id]?.profiles || [])
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ).map(type => ({ value: type, label: FiltersIntegrationType[type] }));

        //themesOptions
        const themesOptions = filter.companies.length
            ? getThemesFilterOptions(
                  themes,
                  filter.companies.map(c => c.id),
                  t,
              )
            : [];

        //ratingOptions
        const ratingOptions = [
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
        ];

        return {
            companies: locationsOptions,
            integrations: integrationsOptions,
            themes: themesOptions,
            rating: ratingOptions,
        };
    }, [filter, locationsMap, themes]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                case 'withoutReplies':
                    increment = filter[key] ? 1 : 0;
                    break;
                case 'onlyFeatured':
                    increment = filterChips[key] ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const updateCompanySensitiveData = newCompaniesIds => {
        const integrations = [
            ...new Set(
                newCompaniesIds
                    .map(selectedItem => locationsMap[selectedItem])
                    .flatMap(location => location?.profiles)
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];

        const themesValues = getThemesFilterOptions(themes, newCompaniesIds).map(t => t.value);

        return { integrations, themesValues };
    };

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];
            const isAllIntegrationsSelected = filter.integrations.length === options.integrations.length;
            const isAllThemesSelected = filter.themes.length === options.themes.length;
            const { integrations, themesValues } = updateCompanySensitiveData(selectedValuesIds);

            setFilter(prev => {
                const newThemes = updateState(prev?.themes, themesValues, isAllThemesSelected);
                return {
                    ...prev,
                    companies: selectedValues,
                    integrations: updateState(prev?.integrations, integrations, isAllIntegrationsSelected),
                    themes: newThemes,
                    ignoreThemes: !themesValues.length || newThemes.length === themesValues.length,
                };
            });
        },
        integrations: value => setFilter(prev => ({ ...prev, integrations: value })),
        themes: value => {
            setFilter(prev => ({
                ...prev,
                themes: value,
                ignoreThemes: value.length === options.themes.length || !options.themes.length || !value.length,
            }));
        },
        rating: value => setFilter(prev => ({ ...prev, rating: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        withoutReplies: e => setFilter(prev => ({ ...prev, withoutReplies: e.target.checked })),
        onlyFeatured: e => setFilter(prev => ({ ...prev, onlyFeatured: e.target.checked })),
    };

    const CHIPS_ACTIONS = {
        companies: {
            reset: () => update.companies(options.companies),
            value: getChipsLabel(filterChips?.companies),
        },
        integrations: {
            reset: () =>
                setFilter(prev => ({
                    ...prev,
                    integrations: updateCompanySensitiveData(prev.companies.map(c => c.id)).integrations,
                })),
            value: getChipsLabel(filterChips?.integrations),
        },
        themes: {
            reset: () =>
                setFilter(prev => ({
                    ...prev,
                    themes: updateCompanySensitiveData(prev.companies.map(c => c.id)).themesValues,
                    ignoreThemes: true,
                })),
            value: getChipsLabel(filterChips?.themes),
        },
        rating: {
            reset: () => setFilter(prev => ({ ...prev, rating: [1, 2, 3, 4, 5] })),
            value: getChipsLabel(filterChips?.rating),
        },
        timeframe: {
            reset: () => setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } })),
            value: filterChips?.timeframe?.start
                ? formatTimeframeChipValue(filterChips.timeframe.start, filterChips.timeframe.end, language)
                : '',
        },
        onlyFeatured: {
            reset: () => setFilter(prev => ({ ...prev, onlyFeatured: false })),
            value: filterChips?.onlyFeatured ? t('ReviewsManage.reviewStatus.featured') : '',
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => {
        Object.values(CHIPS_ACTIONS).forEach(({ reset }) => reset());
        setFilter(prev => ({ ...prev, withoutReplies: false }));
    };

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (key === 'ignoreKeywords' || key === 'ignoreThemes' || key === 'keywords' || key === 'reviewId')
                continue;

            if (Array.isArray(filter[key])) {
                const labelKey = key === 'themes' ? 'categories' : key;
                formOptions.push({
                    type: key === 'companies' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${labelKey}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length && key !== 'themes', // INFO: themes is not required
                });
            } else if (key === 'withoutReplies' || key === 'onlyFeatured') {
                formOptions.push({
                    type: 'switch',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (key === 'keywords' || key === 'ignoreKeywords' || key === 'themes' || key === 'ignoreThemes') {
            return true;
        } else if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    const hasDraftFilters = useMemo(
        () => !isValid || filter.withoutReplies || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        isChipActive(filterChips[key]) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].reset();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
